import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

type DialogInputData = {
  saveLabel?: string;
  cancelLabel?: string;
  discardLabel?: string;
  title?: string;
  message?: string;
  saveDisabledTooltip?: string;
  saveDisabled?: boolean;
  saveHandler$: () => Observable<boolean>;
};

@Component({
  selector: 'rt-prevent-leave-dialog',
  templateUrl: './prevent-leave-dialog.component.html',
  styleUrls: ['./prevent-leave-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    ButtonComponent,
    DialogFooterComponent,
    ButtonLoadingDirective,
    RtTooltip,
    TruncateComponent,
  ],
})
export class PreventLeaveDialogComponent implements DialogComponentI<DialogInputData, boolean> {
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);

  public isLoading = false;
  public saveLabel: string;
  public cancelLabel: string;
  public discardLabel: string;
  public title: string;
  public message: string;
  public saveDisabled: boolean;
  public saveDisabledTooltip: string;
  private saveHandler$: () => Observable<boolean>;

  private defaultTranslations = {
    title: this.i18n.translate('app.prevent_leave_dialog.title'),
    message: this.i18n.translate('app.prevent_leave_dialog.message'),
    saveLabel: this.i18n.translate('app.prevent_leave_dialog.actions.save_changes'),
    cancelLabel: this.i18n.translate('app.prevent_leave_dialog.actions.keep_editing'),
    discardLabel: this.i18n.translate('app.prevent_leave_dialog.actions.discard_changes'),
    saveDisabledTooltip: this.i18n.translate('app.prevent_leave_dialog.save_disabled_tooltip'),
  };

  public setDialogData(data: DialogInputData): void {
    this.saveLabel = data.saveLabel || this.defaultTranslations.saveLabel;
    this.cancelLabel = data.cancelLabel || this.defaultTranslations.cancelLabel;
    this.discardLabel = data.discardLabel || this.defaultTranslations.discardLabel;
    this.title = data.title || this.defaultTranslations.title;
    this.message = data.message || this.defaultTranslations.message;
    this.saveDisabledTooltip =
      data.saveDisabledTooltip || this.defaultTranslations.saveDisabledTooltip;
    this.saveDisabled = data.saveDisabled || false;
    this.saveHandler$ = data.saveHandler$;
  }

  public cancel(): void {
    this.dialogRef.close(false);
  }

  public discardChanges(): void {
    this.dialogRef.close(true);
  }

  public saveChanges(): void {
    this.isLoading = true;
    this.cdRef.detectChanges();
    this.saveHandler$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => this.dialogRef.close(result));
  }
}
